import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './Header.css';
import { services as serviceList, industries as industryList } from '../data/site';

const navItems = [
  { label: 'Services', to: '/services', mega: 'services' },
  { label: 'Industries', to: '/industries', mega: 'industries' },
  { label: 'Case Studies', to: '/case-studies' },
  { label: 'Customers', to: '/customers' },
  { label: 'Insights', to: '/insights' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMega, setActiveMega] = useState(null);
  const headerRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setActiveMega(null);
  }, [location.pathname]);

  const closeMenu = () => {
    setMenuOpen(false);
    setActiveMega(null);
  };

  const onMegaEnter = (mega) => setActiveMega(mega);
  const onMegaLeave = () => setActiveMega(null);
  const onHeaderBlur = (event) => {
    if (!headerRef.current?.contains(event.relatedTarget)) {
      setActiveMega(null);
    }
  };
  const onHeaderKeyDown = (event) => {
    if (event.key === 'Escape') {
      setMenuOpen(false);
      setActiveMega(null);
    }
  };

  return (
    <header className="header" onMouseLeave={onMegaLeave} onBlur={onHeaderBlur} onKeyDown={onHeaderKeyDown} ref={headerRef}>
      <div className="header-strip">
        <div className="site-container header-strip-inner">
          <span className="strip-pill"><span aria-hidden="true">●</span>&nbsp;&nbsp;Now hiring senior practitioners</span>
          <span className="strip-link"><Link to="/case-studies">Representative AI &amp; data outcomes →</Link></span>
        </div>
      </div>

      <div className="header-container">
        <Link to="/" className="brand" aria-label="Dalytix Home" onClick={closeMenu}>
          <span className="brand-mark">
            <img src="/images/dalytix-logo-pro.svg" alt="" />
          </span>
          <span className="brand-copy">
            <strong>Dalytix</strong>
            <small>AI &amp; Data Consulting</small>
          </span>
        </Link>

        <button
          className={`hamburger ${menuOpen ? 'open' : ''}`}
          onClick={() => setMenuOpen((open) => !open)}
          aria-label="Toggle navigation menu"
          aria-expanded={menuOpen}
          aria-controls="main-navigation"
        >
          <span /><span /><span />
        </button>

        <nav className={`nav-links ${menuOpen ? 'active' : ''}`} aria-label="Main navigation" id="main-navigation">
          <ul>
            {navItems.map((item) => (
              <li
                key={item.to}
                className={item.mega ? 'has-mega' : ''}
                onMouseEnter={item.mega ? () => onMegaEnter(item.mega) : undefined}
                onFocus={item.mega ? () => onMegaEnter(item.mega) : undefined}
              >
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  onClick={closeMenu}
                  className={({ isActive }) => (isActive ? 'active-link' : undefined)}
                  aria-haspopup={item.mega ? 'true' : undefined}
                  aria-expanded={item.mega ? activeMega === item.mega : undefined}
                >
                  {item.label}
                  {item.mega && <span className="caret">▾</span>}
                </NavLink>
              </li>
            ))}
            <li>
              <Link to="/request-demo" className="btn-nav" onClick={closeMenu}>
                Book Consultation
              </Link>
            </li>
          </ul>
        </nav>
      </div>

      {activeMega === 'services' && (
        <div className="mega-menu" onMouseLeave={onMegaLeave} role="region" aria-label="Services menu">
          <div className="site-container mega-grid">
            <div className="mega-intro">
              <span className="eyebrow-tag">Services</span>
              <h3>Enterprise AI &amp; Data services from strategy to production</h3>
              <p>
                Nine practices working as one — from board-level strategy and platform engineering to deeply
                regulated, production AI systems.
              </p>
              <Link to="/services" className="btn-light btn-arrow">All services</Link>
            </div>
            <div className="mega-list">
              {serviceList.slice(0, 9).map((s) => (
                <Link to={`/services/${s.id}`} key={s.id} className="mega-item" onClick={closeMenu}>
                  <span className="mega-icon">{s.icon}</span>
                  <strong>{s.title}</strong>
                  <span className="mega-desc">{s.short}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeMega === 'industries' && (
        <div className="mega-menu" onMouseLeave={onMegaLeave} role="region" aria-label="Industries menu">
          <div className="site-container mega-grid">
            <div className="mega-intro">
              <span className="eyebrow-tag">Industries</span>
              <h3>Outcomes built around the realities of your industry</h3>
              <p>
                We work where the business problem is hardest — regulated, real-time, data-intensive, and
                operationally critical.
              </p>
              <Link to="/industries" className="btn-light btn-arrow">All industries</Link>
            </div>
            <div className="mega-list mega-list-3">
              {industryList.slice(0, 11).map((i) => (
                <Link to={`/industries/${i.id}`} key={i.id} className="mega-item mega-item-compact" onClick={closeMenu}>
                  <strong>{i.name}</strong>
                  <span className="mega-desc">{i.summary}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
